import React, { useState, useCallback } from 'react';
import './HotelBooking.css';

function HotelBooking({
  hotel,
  searchParams,
  onSubmit,
  onCancel,
  loading,
}) {
  const [guestDetails, setGuestDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setGuestDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!guestDetails.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!guestDetails.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!guestDetails.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestDetails.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!guestDetails.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10,}$/.test(guestDetails.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Invalid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [guestDetails]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      onSubmit(guestDetails);
    },
    [guestDetails, validateForm, onSubmit]
  );

  const nights = searchParams.checkIn && searchParams.checkOut
    ? Math.ceil(
        (new Date(searchParams.checkOut) -
          new Date(searchParams.checkIn)) /
          (1000 * 60 * 60 * 24)
      )
    : 1;

  const totalPrice = (hotel.lowestRate || 0) * nights;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < rating ? 'star-filled' : 'star-empty'}>
          ★
        </span>
      );
    }
    return stars;
  };

  if (!hotel) {
    return <div className="booking-error">No hotel selected</div>;
  }

  return (
    <div className="hotel-booking">
      <h2>Complete Your Booking</h2>

      <div className="booking-content">
        <div className="booking-summary">
          <h3>Booking Summary</h3>
          <div className="summary-card">
            <div className="summary-header">
              <div>
                <h4>{hotel.name}</h4>
                <div className="summary-rating">
                  <div className="stars-small">
                    {renderStars(hotel.starRating)}
                  </div>
                  <span>{hotel.starRating}/5 stars</span>
                </div>
              </div>
              {hotel.chainName && (
                <span className="chain-badge">{hotel.chainName}</span>
              )}
            </div>

            <div className="summary-details">
              <div className="detail-row">
                <span className="detail-label">Location</span>
                <span className="detail-value">
                  {hotel.address?.city || hotel.city}{hotel.address?.state || hotel.state ? `, ${hotel.address?.state || hotel.state}` : ''}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Check-in</span>
                <span className="detail-value">
                  {formatDate(searchParams.checkIn)}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Check-out</span>
                <span className="detail-value">
                  {formatDate(searchParams.checkOut)}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Duration</span>
                <span className="detail-value">
                  {nights} night{nights !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Guests</span>
                <span className="detail-value">{searchParams.guests}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Room Type</span>
                <span className="detail-value">
                  {hotel.rooms?.[0]?.type || 'Standard Room'}
                </span>
              </div>

              {hotel.rooms?.[0]?.cancellationPolicy && (
                <div className="detail-row">
                  <span className="detail-label">Cancellation Policy</span>
                  <span className="detail-value">
                    {hotel.rooms[0].cancellationPolicy}
                  </span>
                </div>
              )}

              <div className="detail-row divider"></div>

              <div className="detail-row price-row">
                <span className="detail-label">Rate per night</span>
                <span className="detail-value">
                  ${hotel.lowestRate?.toFixed(2) || '0.00'}
                </span>
              </div>

              <div className="detail-row price-row">
                <span className="detail-label">
                  Total for {nights} night{nights !== 1 ? 's' : ''}
                </span>
                <span className="detail-value price-highlight">
                  ${totalPrice.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="booking-form-section">
          <h3>Guest Information</h3>
          <form className="booking-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={guestDetails.firstName}
                  onChange={handleChange}
                  disabled={loading}
                  className={errors.firstName ? 'input-error' : ''}
                  placeholder="John"
                />
                {errors.firstName && (
                  <span className="error-text">{errors.firstName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={guestDetails.lastName}
                  onChange={handleChange}
                  disabled={loading}
                  className={errors.lastName ? 'input-error' : ''}
                  placeholder="Smith"
                />
                {errors.lastName && (
                  <span className="error-text">{errors.lastName}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                id="email"
                name="email"
                type="email"
                value={guestDetails.email}
                onChange={handleChange}
                disabled={loading}
                className={errors.email ? 'input-error' : ''}
                placeholder="john.smith@example.com"
              />
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={guestDetails.phone}
                onChange={handleChange}
                disabled={loading}
                className={errors.phone ? 'input-error' : ''}
                placeholder="+1 (555) 123-4567"
              />
              {errors.phone && (
                <span className="error-text">{errors.phone}</span>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="button-secondary"
                onClick={onCancel}
                disabled={loading}
              >
                ← Cancel
              </button>
              <button
                type="submit"
                className="button-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-small"></span>
                    Processing...
                  </>
                ) : (
                  'Confirm Booking'
                )}
              </button>
            </div>
          </form>

          <div className="booking-note">
            <p>
              A confirmation email will be sent to <strong>{guestDetails.email || 'your email'}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HotelBooking;
